import React, { Suspense, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useController } from '@rest-hooks/react';

import StripePaymentForm from 'components/CheckoutModal/PaymentModal/PaymentCollection/StripePaymentForm';
import EupagoPaymentForm from 'components/CheckoutModal/PaymentModal/PaymentCollection/EupagoPaymentForm';
import PaypalPaymentForm from 'components/CheckoutModal/PaymentModal/PaymentCollection/PaypalPaymentForm';
import { handleValidation } from 'components/CheckoutModal/PaymentModal/PaymentCollection/handleValidation';
import LoadingComponent from 'components/CheckoutModal/PaymentModal/LoadingComponent';

import ShippingAddressForm from 'components/ShippingAddressForm';

import { useError } from 'utils/useErrorController';
import usePaymentIntent from 'utils/usePaymentIntent';
import shouldShowProviderTabs from 'utils/shouldShowProviderTabs';
import useStripe from 'utils/useStripe';
import useEupago from 'utils/useEupago';
import { findOrderResult } from 'utils/profile/claim/findOrderInstance';

import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import { t } from 'i18n/index';

const CartPaymentCollection = ({
  cartItems,
  userPhone,
  onUserPhoneChange,
  stripeState,
  onStripeStateChange,
  orderState,
  onOrderChange,
  onStepChange,
  onContentChange,
  onShowTickets,
}) => {

  const { setLoading, loading } = useError();
  const { fetch } = useController();

  const { selectedProvider, providers } = orderState;

  // SELL05 — one address for the whole cart, whichever line made it
  // deliverable. Tolerates stale sessionStorage carts with no `deliverable`
  // key: the server-side raise still blocks the order and surfaces here.
  // SELL20 — store lines only: event-page lines (they carry a listing id) are
  // collected at the venue and never ask for an address.
  const requiresShipping = cartItems.some((ci) => ci.product?.deliverable && !ci.product?.event_product_listing_id);
  const [shippingAddressRequiredByServer, setShippingAddressRequiredByServer] = useState(false);
  const [shippingAddress, setShippingAddress] = useState(null);
  const awaitingShippingAddress = (requiresShipping || shippingAddressRequiredByServer) && !shippingAddress;

  const showTabs = shouldShowProviderTabs({ providers, awaitingShippingAddress });

  const handleProviderChange = (provider) => {
    const newOrderState = { selectedProvider: provider, paymentIntentId: '' };
    onOrderChange((previousValue) => ({ ...previousValue, ...newOrderState }));
  };

  const {
    stripe,
    handleStripeCardPaymentIntent,
  } = useStripe({
    onStripeStateChange,
    onOrderChange,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setOrderPropertiesState: () => {}, // cart forms don't use orderPropertiesState
  });

  const {
    processingEupago,
    handleEupagoPaymentIntent,
    handleErrorFindingOrderResult,
  } = useEupago({
    onOrderChange,
    onUserPhoneChange,
  });

  const handlePaypalPaymentIntent = (id) => {
    onOrderChange((previousValue) => ({ ...previousValue, paymentIntentId: id }));
  };

  const {
    createPaymentIntent,
  } = usePaymentIntent({
    handleValidation,
    userPhone,
    object: cartItems[0]?.product, // for organization_id
    pricingId: null,
    requestedAmount: null,
    handleStripeCardPaymentIntent,
    handleEupagoPaymentIntent,
    handlePaypalPaymentIntent,
    cartItems,
    shippingAddress,
    onShippingAddressRequired: () => setShippingAddressRequiredByServer(true),
  });

  const handleEupagoFormSubmission = async (e, payment_method) => {
    e.preventDefault();
    e.stopPropagation();

    if (processingEupago) {
      setLoading(true);
      findOrderResult(processingEupago, fetch, onStepChange, handleErrorFindingOrderResult, setLoading, onContentChange, onShowTickets);
    } else {
      createPaymentIntent("eupago", payment_method);
    }
  };

  useEffect(() => {
    if (selectedProvider === "stripe" && !awaitingShippingAddress) createPaymentIntent("stripe");
  }, [selectedProvider, shippingAddress]);

  const isBizumOrganization = cartItems[0]?.product?.organization?.id === 1;

  const tabLabels = {
    "stripe": t('literal:card'),
    "eupago": isBizumOrganization ? "Bizum" : "MBWay",
    "paypal": "Paypal",
  };

  return (
    <>
      {showTabs &&
        <Box className="tabpanel-menu slim" sx={{ borderBottom: 1, borderColor: 'var(--borderColor)' }}>
          <Tabs value={providers.indexOf(selectedProvider)}>
            {providers.map((provider, index) => (
              <Tab
                key={index}
                label={tabLabels[provider]}
                disabled={loading || selectedProvider === provider}
                onClick={() => handleProviderChange(provider)}
              />
            ))}
          </Tabs>
        </Box>
      }

      {!selectedProvider &&
        <LoadingComponent />
      }

      {selectedProvider && awaitingShippingAddress && (
        <ShippingAddressForm onSubmit={setShippingAddress} />
      )}

      {/* Local boundary: the payment footer suspends on its first
          user_language fetch after page load — without this, the fallback
          unwinds to the profile body's full-page boundary and the whole page
          flashes blank while the intent is being created. */}
      <Suspense fallback={<LoadingComponent />}>
        {!awaitingShippingAddress && selectedProvider === "stripe" && (
          <StripePaymentForm
            items={cartItems}
            onStepChange={onStepChange}
            onContentChange={onContentChange}
            stripeState={stripeState}
            onShowTickets={onShowTickets}
            stripe={stripe}
          />
        )}

        {!awaitingShippingAddress && selectedProvider === "paypal" && (
          <PaypalPaymentForm
            items={cartItems}
            createOrder={() => createPaymentIntent("paypal")}
            onShowTickets={onShowTickets}
            onStepChange={onStepChange}
            onContentChange={onContentChange}
          />
        )}

        {!awaitingShippingAddress && selectedProvider === "eupago" && (
          <EupagoPaymentForm
            items={cartItems}
            userPhone={userPhone}
            onUserPhoneChange={onUserPhoneChange}
            onSubmit={handleEupagoFormSubmission}
            processingEupago={processingEupago}
            redirectUrl={orderState.redirectUrl}
            isBizumOrganization={isBizumOrganization}
          />
        )}
      </Suspense>
    </>
  );
};

CartPaymentCollection.propTypes = {
  cartItems: PropTypes.array.isRequired,
  userPhone: PropTypes.string,
  onUserPhoneChange: PropTypes.func,
  stripeState: PropTypes.object,
  onStripeStateChange: PropTypes.func,
  orderState: PropTypes.object,
  onOrderChange: PropTypes.func,
  onStepChange: PropTypes.func,
  onContentChange: PropTypes.func,
  onShowTickets: PropTypes.func,
};

export default CartPaymentCollection;
